import React from 'react';

import { cn } from '@easydatasolution/ui-react/lib/utils';
import { Link } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { useCmsData } from '../../hooks/use-cms-data';
import { useSettings } from '../../hooks/use-settings';
import { Url } from '../../misc/enums';
import { Button } from '../Button/Button';
import { Logo } from '../Logo/Logo';

interface FooterLinkProps {
	children: React.ReactNode;
	className?: string;
}

const FooterLinksHeader: React.FC<FooterLinkProps> = ({ children, className }) => (
	<p className={cn('text-lg font-semibold text-secondary-700', className)}>{children as string}</p>
);

interface Page {
	node: {
		id: string;
		frontmatter: {
			slug: string;
			title: string;
			navigationTitle?: string;
			uuid?: string;
		};
	};
}

interface FooterSubLinksProps {
	pages: Page[];
	toPrefix?: string;
	parentSlug?: string;
}

const FooterSubLinks: React.FC<FooterSubLinksProps> = ({ pages, toPrefix = '', parentSlug }) =>
	pages.map(({ node }) => (
		<FooterSubLink
			key={node.id}
			to={
				parentSlug
					? `${toPrefix}/${parentSlug}/${node.frontmatter.slug}/`
					: `${toPrefix}/${node.frontmatter.slug}/`
			}
		>
			{node.frontmatter.navigationTitle ?? node.frontmatter.title}
		</FooterSubLink>
	));

interface FooterSubLinkProps {
	children: React.ReactNode;
	to: string;
}

const FooterSubLink: React.FC<FooterSubLinkProps> = ({ children, to }) => (
	<Link to={to} className="text-secondary-700">
		{children as string}
	</Link>
);
interface FooterProps {
	showContactSection?: boolean;
}

export const Footer: React.FC<FooterProps> = ({ showContactSection = false }) => {
	const { courseTypes, digitalizationPages } = useCmsData();
	const { digitalizationOrder, coursesOrder } = useSettings();

	const orderedDigitalizationPages = digitalizationOrder.entries
		.flatMap(({ digitalizationParent, digitalizationChildren }) => {
			const parent = digitalizationPages.find(
				({ node: { frontmatter: digitalizationPage } }) =>
					digitalizationPage.uuid === digitalizationParent,
			);

			const children = digitalizationChildren
				.map((childUuid) =>
					digitalizationPages.find(
						({ node: { frontmatter: digitalizationPage } }) =>
							digitalizationPage.uuid === childUuid,
					),
				)
				.filter(Boolean)
				.map((page) => ({
					...page,
					parentSlug: parent?.node.frontmatter.slug,
				}));

			return [parent, ...children];
		})
		.filter(Boolean);

	const orderedCourseTypes = [
		...coursesOrder.entries
			.filter(({ courseType }) => !coursesOrder.otherCourseTypes.includes(courseType))
			.map(({ courseType }) => {
				return courseTypes.find(
					({ node: { frontmatter: courseTypePage } }) => courseTypePage.uuid === courseType,
				);
			}),
		...coursesOrder.entries
			.filter(({ courseType }) => coursesOrder.otherCourseTypes.includes(courseType))
			.map(({ courseType }) => {
				return courseTypes.find(
					({ node: { frontmatter: courseTypePage } }) => courseTypePage.uuid === courseType,
				);
			}),
	].filter(Boolean);

	return (
		<>
			{showContactSection && (
				<section className="relative z-50 mx-auto -mb-12 flex max-w-[1140px] flex-col justify-between gap-8 rounded-lg px-12 py-10 md:flex-row md:items-center md:gap-12">
					<div
						className="absolute inset-0 bg-white md:rounded-lg"
						style={{ bottom: '-16px' }}
					></div>
					<div className="relative z-10">
						<h2 className="text-pretty text-2xl font-semibold text-secondary-700">
							Wir sind nur einen Klick entfernt!
						</h2>
						<p className="text-secondary-700">Nehmen Sie unkompliziert Kontakt zu uns auf.</p>
					</div>
					<Button as={Link} to={`/${Url.CONTACT}/`} className="rounded-lg px-20">
						Kontakt
					</Button>
				</section>
			)}
			<footer className="relative overflow-hidden pb-10 pt-32">
				<div className="container">
					<div className="grid grid-cols-1 items-start justify-items-center gap-10 md:grid-cols-[auto_auto] md:justify-items-start md:gap-8 lg:grid-cols-[auto_auto_auto_auto]">
						<Logo className="w-[280px] self-center" />
						<div className="hidden flex-col items-center md:flex md:items-start lg:hidden">
							<FooterLinksHeader>Info</FooterLinksHeader>
							<div className="mt-3 flex flex-col items-center gap-2 md:items-start">
								<FooterSubLink to={`/${Url.CONTACT}/`}>Kontakt</FooterSubLink>
								<FooterSubLink to={`/${Url.IMPRINT}/`}>Impressum</FooterSubLink>
								<FooterSubLink to={`/${Url.DATA_PRIVACY}/`}>Datenschutzerklärung</FooterSubLink>
								<FooterSubLink to={`/${Url.AGB}/`}>AGBs</FooterSubLink>
							</div>
						</div>

						<div className="flex flex-col items-center md:items-start">
							<FooterLinksHeader>Kurse</FooterLinksHeader>
							<div className="mt-3 flex flex-col items-center gap-2 md:items-start">
								<FooterSubLinks pages={orderedCourseTypes} toPrefix={`/${Url.COURSES}`} />
							</div>
						</div>

						<div className="flex flex-col items-center md:items-start">
							<FooterLinksHeader>Digitalisierung</FooterLinksHeader>
							<div className="mt-3 flex flex-col items-center gap-2 md:items-start">
								{orderedDigitalizationPages.map((page) => {
									if ('parentSlug' in page) {
										return (
											<FooterSubLinks
												key={page.node.id}
												pages={[page]}
												toPrefix={`/${Url.DIGITALIZATION}`}
												parentSlug={page.parentSlug}
											/>
										);
									}
									return (
										<FooterSubLinks
											key={page.node.id}
											pages={[page]}
											toPrefix={`/${Url.DIGITALIZATION}`}
										/>
									);
								})}
							</div>
						</div>

						<div className="flex flex-col items-center md:hidden md:items-start lg:flex">
							<FooterLinksHeader>Info</FooterLinksHeader>
							<div className="mt-3 flex flex-col items-center gap-2 md:items-start">
								<FooterSubLink to={`/${Url.CONTACT}/`}>Kontakt</FooterSubLink>
								<FooterSubLink to={`/${Url.IMPRINT}/`}>Impressum</FooterSubLink>
								<FooterSubLink to={`/${Url.DATA_PRIVACY}/`}>Datenschutzerklärung</FooterSubLink>
								<FooterSubLink to={`/${Url.AGB}/`}>AGBs</FooterSubLink>
							</div>
						</div>
					</div>
				</div>
				{/* absolute positioning does not work on the image itself, see https://github.com/gatsbyjs/gatsby/issues/5727#issuecomment-1081479991 */}
				<div className="absolute inset-0 -z-20 opacity-35">
					<StaticImage
						src="../../../static/images/hero_background.png"
						alt=""
						className="h-full w-full"
					/>
				</div>
			</footer>
		</>
	);
};
